import React, { useContext, useState, useMemo } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import AccountCircle from '@mui/icons-material/AccountCircle';
import ButtonBase from '@mui/material/ButtonBase';
import Paper from '@mui/material/Paper';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Chip from '@mui/material/Chip';
import ListSubheader from '@mui/material/ListSubheader';
import { useNavigate } from 'react-router-dom';
import { MapContext } from '../../contexts/MapContext';
import { clearJwt } from '../../services/BaseApiService';
import { getUserData, getUserBalance, getUserActivityLogs } from '../../services/AuthService';
import { logUserLogout } from '../../services/ActivityLogService';
import { VERSION, VERSION_DESCRIPTION } from '../../config/appConfig';

const activityTypeColorMap = {
  login: 'success',
  logout: 'default',
  search: 'info',
  visualizar: 'warning',
  erro: 'error',
  teste: 'secondary',
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function toStartOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseTimestamp(value) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateLabel(date) {
  const todayStart = toStartOfDay(new Date());
  const dateStart = toStartOfDay(date);
  const diffInDays = Math.round((todayStart.getTime() - dateStart.getTime()) / MS_PER_DAY);

  if (diffInDays === 0) {
    return 'Hoje';
  }

  if (diffInDays === 1) {
    return 'Ontem';
  }

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatTimestampLabel(timestamp) {
  const parsed = parseTimestamp(timestamp);

  if (!parsed) {
    return 'Horário desconhecido';
  }

  const time = parsed.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const date = parsed.toLocaleDateString('pt-BR');
  return `${time} · ${date}`;
}

function getDateKey(date) {
  return toStartOfDay(date).toISOString();
}

function getChipColor(activityType) {
  if (!activityType) {
    return 'default';
  }

  return activityTypeColorMap[activityType.toLowerCase()] || 'default';
}

function TopBar() {
  const navigate = useNavigate();
  const {
    setMarker,
    setProperty,
    setSidebarOpen,
    setGeometries,
    setThemeGeometries,
    setActiveLayers,
    setPropertyCodes,
  } = useContext(MapContext);

  const [popupOpen, setPopupOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState(null);
  const [userBalance, setUserBalance] = useState(null);
  const [balanceError, setBalanceError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);

  const groupedActivities = useMemo(() => {
    if (!activityLogs || activityLogs.length === 0) {
      return [];
    }

    const groups = new Map();

    activityLogs.forEach((log) => {
      const parsedDate = parseTimestamp(log.timestamp);
      const hasValidDate = Boolean(parsedDate);
      const dateKey = hasValidDate ? getDateKey(parsedDate) : 'invalid';
      const label = hasValidDate ? formatDateLabel(parsedDate) : 'Data desconhecida';
      const sortValue = hasValidDate ? toStartOfDay(parsedDate).getTime() : Number.MIN_SAFE_INTEGER;

      if (!groups.has(dateKey)) {
        groups.set(dateKey, {
          key: dateKey,
          label,
          sortValue,
          items: [],
        });
      }

      groups.get(dateKey).items.push(log);
    });

    const sortedGroups = Array.from(groups.values()).sort((a, b) => b.sortValue - a.sortValue);

    sortedGroups.forEach((group) => {
      group.items.sort((a, b) => {
        const dateA = parseTimestamp(a.timestamp);
        const dateB = parseTimestamp(b.timestamp);

        if (dateA && dateB) {
          return dateB.getTime() - dateA.getTime();
        }

        if (dateA) {
          return -1;
        }

        if (dateB) {
          return 1;
        }

        return 0;
      });
    });

    return sortedGroups;
  }, [activityLogs]);

  const handleHomeClick = () => {
    setMarker(null);
    setProperty(null);
    setSidebarOpen(false);
    setGeometries([]);
    setThemeGeometries({});
    setActiveLayers({});
    setPropertyCodes({});
    navigate('/');
  };

  const togglePopup = async () => {
    setPopupOpen(!popupOpen);

    if (!popupOpen) {
      try {
        // Carregar dados do usuário
        const data = await getUserData();
        setUserData(data);
        setError(null);

        // Resetar a tab para "Perfil"
        setActiveTab(0);

        // Carregar saldo
        try {
          const balanceData = await getUserBalance();
          setUserBalance(balanceData);
          setBalanceError(null);
        } catch (_e) {
          setUserBalance(null);
          setBalanceError('Erro ao carregar saldo');
        }
      } catch (_err) {
        setError('Erro ao carregar os dados do usuário');
        clearJwt();
        navigate('/login');
      }
    }
  };

  // Função para carregar logs de atividade quando a tab for alterada
  const handleTabChange = async (event, newValue) => {
    setActiveTab(newValue);

    // Se a tab de atividades foi selecionada, carregar os logs
    if (newValue === 1 && activityLogs.length === 0) {
      try {
        setActivityLoading(true);
        setActivityError(null);
        const logs = await getUserActivityLogs(30); // Buscar logs dos últimos 30 dias
        setActivityLogs(logs);
      } catch (_error) {
        setActivityError('Erro ao carregar as atividades');
      } finally {
        setActivityLoading(false);
      }
    }
  };

  const handleLogout = async () => {
    // Registrar o logout antes de limpar as credenciais
    try {
      // Verificar se temos os dados do usuário
      if (userData && userData.id) {
        await logUserLogout(userData.id, userData.email);
      }
    } catch (error) {
      // Apenas logar o erro, mas continuar com o processo de logout
      console.error('Erro ao registrar logout:', error);
    } finally {
      // Continuar com o processo de logout mesmo se houver erro no registro
      clearJwt();
      setPopupOpen(false); // Fecha o popup
      navigate('/login');
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="fixed" color="primary">
        <Toolbar>
          <Box
            sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-start' }}
          >
            <ButtonBase
              onClick={handleHomeClick}
              title="Ir para o início"
              sx={{ p: 0 }}
              disableRipple
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ color: 'inherit', userSelect: 'none', textAlign: 'left', lineHeight: 1 }}
                >
                  Green:Nexus - Análise Fundiária
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ color: 'inherit', userSelect: 'none', textAlign: 'left', mt: 0.5 }}
                >
                  v{VERSION} - {VERSION_DESCRIPTION}
                </Typography>
              </Box>
            </ButtonBase>
          </Box>
          <IconButton color="inherit" sx={{ ml: 2 }} onClick={togglePopup}>
            <AccountCircle />
          </IconButton>
        </Toolbar>
      </AppBar>

      {popupOpen && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 64,
            right: 16,
            width: 360,
            height: 480,
            zIndex: 1300,
            padding: 2,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 3,
          }}
        >
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="body1">Meu Perfil</Typography>
            <IconButton size="small" onClick={togglePopup}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Tabs para navegação entre Perfil e Atividades */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="Perfil" />
            <Tab label="Atividades" />
          </Tabs>

          {/* Conteúdo da Tab Perfil */}
          {activeTab === 0 && (
            <>
              <Box sx={{ flexGrow: 1, mt: 2 }}>
                {error ? (
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                ) : userData ? (
                  <>
                    <Typography variant="body2">
                      <strong>Nome:</strong> {userData.name}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Email:</strong> {userData.email}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Documento:</strong> {userData.document}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Telefone:</strong> {userData.phone}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Status:</strong> {userData.status}
                    </Typography>
                    {userData.id && (
                      <Typography variant="body2">
                        <strong>ID:</strong> {userData.id}
                      </Typography>
                    )}
                  </>
                ) : (
                  <Typography variant="body2">Carregando...</Typography>
                )}
              </Box>

              {/* Bloco de saldo do cliente */}
              <Box sx={{ flexGrow: 0, mt: 2, mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Saldo
                </Typography>
                {balanceError ? (
                  <Typography variant="body2" color="error">
                    {balanceError}
                  </Typography>
                ) : userBalance ? (
                  <>
                    <Typography variant="body2">
                      <strong>Saldo:</strong> {userBalance.balance}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Tipo de saldo:</strong> {userBalance.balance_type}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2">Carregando saldo...</Typography>
                )}
              </Box>
            </>
          )}

          {/* Conteúdo da Tab Atividades */}
          {activeTab === 1 && (
            <Box sx={{ flexGrow: 1, mt: 2, overflowY: 'auto', maxHeight: 300 }}>
              {activityError ? (
                <Typography variant="body2" color="error">
                  {activityError}
                </Typography>
              ) : activityLoading ? (
                <Typography variant="body2">Carregando atividades...</Typography>
              ) : groupedActivities.length > 0 ? (
                <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
                  {groupedActivities.map((group, groupIndex) => (
                    <React.Fragment key={group.key}>
                      <ListSubheader component="div" sx={{ bgcolor: 'background.paper', px: 0 }}>
                        {group.label}
                      </ListSubheader>
                      {group.items.map((log, index) => (
                        <React.Fragment key={log.log_id || `${group.key}-${index}`}>
                          <ListItem
                            alignItems="flex-start"
                            sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1.5 }}
                          >
                            <Box
                              sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 0.5 }}
                            >
                              <Chip
                                label={log.activity_type || 'Ação'}
                                size="small"
                                color={getChipColor(log.activity_type)}
                                sx={{ textTransform: 'capitalize', mr: 1 }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {formatTimestampLabel(log.timestamp)}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.primary">
                              {log.description || 'Sem descrição'}
                            </Typography>
                          </ListItem>
                          {index < group.items.length - 1 && <Divider component="div" />}
                        </React.Fragment>
                      ))}
                      {groupIndex < groupedActivities.length - 1 && (
                        <Divider sx={{ mt: 1.5, mb: 1 }} />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2">
                  Nenhuma atividade registrada nos últimos 30 dias.
                </Typography>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'auto' }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setPopupOpen(false);
                navigate('/profile');
              }}
              sx={{ mt: 2 }}
            >
              Editar Perfil
            </Button>
            <Button variant="contained" color="secondary" onClick={handleLogout} sx={{ mt: 2 }}>
              Sair
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
}

export default TopBar;
